import React, { FC } from "react";
import { JobPostRemoteType, Location } from "@prisma/client";
import { useFormContext } from "react-hook-form";
import dynamic from "next/dynamic";
import { JobPostEditorSectionLayout } from "@components/Layout/JobPostEditorSectionLayout";
import { GoogleMapsApiProvider } from "@components/PlacesAutocomplete/GoogleMapsApiProvider";
import { AsyncAutocompleteRemoteRegions } from "@components/AsyncAutocomplete/AsyncAutocompleteRemoteRegions";
import { NewTag, TagType } from "types/tags";
import { TagsGroup } from "@components/TagsGroup/TagsGroup";

// dynamic import
const PlacesAutocomplete = dynamic(() =>
  import("../../../PlacesAutocomplete/PlacesAutocomplete").then((module) => {
    return module.PlacesAutocomplete;
  })
);

interface Props {
  tags: NewTag[];
}

export const LocationSection: FC<Props> = ({ tags }) => {
  // form
  const {
    watch,
    getValues,
    setValue,
    register,
    control,
    formState: { errors },
  } = useFormContext();
  // ONSITE
  const hasLocations = watch("jobPost.hasLocations");
  const locations = watch("jobPost.locations");
  // REMOTE
  const hasRemote = watch("jobPost.hasRemote");
  const jobPost = getValues("jobPost");
  const remoteType = watch("jobPost.remote.type");
  const remoteRegions = watch("jobPost.remote.regions");
  const remoteRequirementsTypeSettings = [
    {
      name: "Not available.",
      value: JobPostRemoteType.NONE,
    },
    {
      name: "Available Worldwide.",
      value: JobPostRemoteType.WORLDWIDE,
    },
    {
      name: "Available in selected countries or time zones.",
      value: JobPostRemoteType.SELECTED_REGIONS,
    },
  ];

  // methods
  const addLocation = () => {
    const ts = new Date().getTime();
    const newLocations = [
      ...(locations ? locations : []),
      {
        id: `new-${ts}`,
        address: "",
      },
    ];
    setValue("jobPost.locations", newLocations);
  };

  const updateLocation = (id: Location["id"], location: Partial<Location>) => {
    const newLocations = locations.map((l) => {
      if (l.id === id) {
        return {
          ...l,
          ...location,
        };
      }
      return l;
    });
    setValue("jobPost.locations", newLocations);
  };

  const removeLocation = (id: Location["id"]) => {
    const newLocations = locations.filter((location) => location.id !== id);
    setValue("jobPost.locations", newLocations);
  };

  return (
    <JobPostEditorSectionLayout
      title="Onsite / Remote"
      description="Provide workplace location(s). If the job is fully remote, you can list the HQ location if there is one."
      hasToggle
      isToggled={hasLocations}
      onToggle={() => setValue("jobPost.hasLocations", !hasLocations)}
    >
      {/* TAGS */}
      <div className="mb-6">
        <TagsGroup
          heading="Does any of this apply?"
          availableTagTypes={[
            TagType.COMPENSATION_BENEFIT,
            TagType.ORGANISATION,
          ]}
          tags={tags.filter((t) =>
            ["remote-first", "paid-relocation", "visa-sponsorship"].includes(
              t.id
            )
          )}
          name="jobPost.userAssignedTags"
        />
      </div>

      {/* ONSITE */}
      <div className="p-4 bg-gray-50 border rounded-md">
        <div className="mb-4 text-sm font-bold uppercase">Onsite location</div>
        <GoogleMapsApiProvider>
          {locations?.length > 0
            ? locations?.map((loc, index) => {
                return (
                  <div key={loc.id} className="pb-4">
                    <label
                      htmlFor="jobPost.locations.address"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Location #{index + 1}
                    </label>
                    <div className="flex flex-row justify-start items-start">
                      {/* @ts-ignore:next-line */}
                      <PlacesAutocomplete
                        name={loc.id}
                        defaultValue={loc?.address || ""}
                        control={control}
                        cN="relative w-full z-10"
                        inputCN="mt-1 focus:ring-blue-500 focus:border-blue-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        optionsCN="mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm"
                        optionCN="hover:text-white hover:bg-blue-600 cursor-default select-none relative py-2 pl-3 pr-9"
                        onSubmit={(value) => {
                          updateLocation(loc.id, { address: value });
                        }}
                      />
                      <button
                        className="m-2 p-1 text-xxs font-semibold uppercase text-gray-400 underline"
                        type="button"
                        onClick={() => removeLocation(loc.id)}
                      >
                        remove
                      </button>
                    </div>
                  </div>
                );
              })
            : null}
          <button
            className="px-2 py-1 text-xxs font-semibold uppercase text-blue-600 border border-blue-600 rounded hover:bg-blue-100"
            type="button"
            onClick={addLocation}
          >
            Add onsite location
          </button>
        </GoogleMapsApiProvider>
      </div>

      {/* REMOTE */}
      <div className="mt-6 p-4 bg-gray-50 border rounded-md">
        <div className="mb-4 text-sm font-bold uppercase">
          Remote available?
        </div>
        <fieldset>
          <div className="space-y-4">
            {remoteRequirementsTypeSettings.map((setting) => (
              <div key={setting.value} className="flex items-center">
                <input
                  id={setting.value}
                  name="remoteRequirementsType"
                  type="radio"
                  defaultChecked={setting.value === jobPost?.remote?.type}
                  className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300"
                  onClick={() => setValue("jobPost.remote.type", setting.value)}
                />
                <label
                  htmlFor={setting.value}
                  className="ml-3 block text font-medium text-gray-700"
                >
                  {setting.name}
                </label>
              </div>
            ))}
          </div>
        </fieldset>

        {remoteType === JobPostRemoteType.SELECTED_REGIONS && (
          <div className="flex flex-row space-x-2 pt-4 pl-7">
            <div className="w-full">
              {/* <label className="block mb-1 text-sm font-medium text-gray-700">
              Accepting candidates from the following countries, regions or time
              zones
            </label> */}
              <div className="bg-white">
                <AsyncAutocompleteRemoteRegions
                  id="jobPost.remote.regions"
                  value={remoteRegions}
                  setValue={(value) =>
                    setValue("jobPost.remote.regions", value)
                  }
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </JobPostEditorSectionLayout>
  );
};
